import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDateShort, formatTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CashTimeline } from "./CashTimeline";
import { Loader2 } from "lucide-react";

export default function CashSessionDetailDialog({ sessionId, open, onOpenChange }) {
  const { orgId } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["cash-session", orgId, sessionId],
    queryFn: async () => (await api.get(`/api/cash/sessions/${sessionId}`, { params: { organization_id: orgId } })).data,
    enabled: open && !!sessionId,
  });
  const currency = data?.currency || "MAD";
  const t = data?.totals || {};
  const m = (c) => formatMoney(c || 0, currency);
  const diff = data?.closing_difference_cents;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="cash-session-detail-dialog">
        <DialogHeader>
          <DialogTitle>Détail de la caisse</DialogTitle>
          <DialogDescription className="sr-only">Résumé et historique d'une session de caisse.</DialogDescription>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Info k="Ouverture" v={`${formatDateShort(data.opened_at)} ${formatTime(data.opened_at)}`} />
              <Info k="Ouvert par" v={data.opened_by_name} />
              <Info k="Fermeture" v={data.closed_at ? `${formatDateShort(data.closed_at)} ${formatTime(data.closed_at)}` : "—"} />
              <Info k="Fermé par" v={data.closed_by_name || "—"} />
            </div>
            <div className="space-y-1.5 rounded-lg bg-slate-50 p-3 text-sm">
              <Row k="Fond de caisse" v={m(t.opening_cash_cents)} />
              <Row k="Encaissements espèces" v={m(t.cash_payments_cents)} />
              <Row k="Dépenses espèces" v={`-${m(t.cash_expenses_cents)}`} />
              <Row k="Dépôts" v={m(t.deposits_cents)} />
              <Row k="Retraits" v={`-${m(t.withdrawals_cents)}`} />
              <div className="flex items-center justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900"><span>Caisse théorique</span><span>{m(t.expected_cash_cents)}</span></div>
              {data.status === "CLOSED" && (
                <>
                  <Row k="Caisse comptée" v={m(data.actual_closing_cash_cents)} />
                  <div className={`flex items-center justify-between font-semibold ${diff === 0 ? "text-emerald-700" : "text-amber-700"}`}>
                    <span>Écart</span><span data-testid="session-detail-difference">{diff > 0 ? "+" : ""}{m(diff)}</span>
                  </div>
                  {data.closing_note && <p className="pt-1 text-xs text-slate-500">Motif : {data.closing_note}</p>}
                </>
              )}
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold text-slate-700">Mouvements</p>
              <CashTimeline events={data.timeline} currency={currency} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => (<div className="flex items-center justify-between"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>);
const Info = ({ k, v }) => (<div><p className="text-xs text-slate-400">{k}</p><p className="font-medium text-slate-800">{v}</p></div>);
